import { TokenTypes } from '../store/TokenStore';
import { BaseService, RequestMethod } from './BaseService';

export namespace AuthorizationService {

  export type IAuthorizedRequestAction = 'read' | 'purchase';
  export interface IAuthorizedRequest {
    client: string;
    type?: string;
    id: number | string;
    action: IAuthorizedRequestAction;
  }
  export interface IServiceAuthorizedRequest {
    client: string;
    userId: string;
    type?: string;
    id: number | string;
    action: IAuthorizedRequestAction;
  }
  export interface IImpersonationRequest {
    accountId: string;
    authSource: string;
    authScope: string;
  }
  export interface IAttributesData {
    id: number | string;
    type: string;
    userId: string;
  }

  export interface IAttributesRequest {
    data: IAttributesData;
  }

  export interface ICreateAttributesDataRequest extends IAttributesData {
    permission: 'read' | 'write' | string;
    expiresAt?: string;
  }

  export interface ICreateAttributesRequest {
    data: ICreateAttributesDataRequest;
  }

  export interface IDestroyAttributesRequest {
    data: IAttributesData;
  }

  export interface IUpdateAttributesRequest {
    data: IAttributesData;
  }

  export interface IAuthorizedResponse {
    readonly authorized: boolean;
  }

  /*
  export interface IImpersonateTokens {
    readonly id_token: string;
    readonly access_token?: string;
    readonly refresh_token?: string;
  }
  */
  export interface IImpersonationResponse {
    //readonly tokens: IImpersonateTokens;
    readonly tokens: Record<string, string>;
  }

  export type IAttributesRequests = ICreateAttributesRequest | IDestroyAttributesRequest | IUpdateAttributesRequest;

  export interface IUsers {
    [key: string]: {
      [key: string]: (request: IAttributesRequests) => Promise<any>;
    };
  }

  export interface IService {
    [key: string]: (request: IServiceAuthorizedRequest) => Promise<any>;
  }

  export class Api extends BaseService {
    constructor() {
      super({ configPath: 'services.authorization' });
    }

    get users(): IUsers {
      const functions: { [key: string]: RequestMethod } = {
        create: RequestMethod.POST,
        destroy: RequestMethod.DELETE,
        update: RequestMethod.PUT,
      };

      const attributes: { [key: string]: (request: IAttributesRequests) => any } = {};

      for (const f of Object.keys(functions)) {
        attributes[f] = (request: IAttributesRequests) => {
          const { data } = request;

          return this.requestResource(`service.users.attributes.${f}`, {
            method: RequestMethod[functions[f]],
            sign: true,
            data,
          });
        };
      }

      return {
        attributes,
      };
    }

    get service(): IService {
      return {
        authorized: (request: IServiceAuthorizedRequest): Promise<IAuthorizedResponse> => {
          const data = <unknown>request as Record<string, unknown>;
          return this.requestResource('service.authorize', {
            method: RequestMethod.POST,
            sign: true,
            tokenType: 'none',
            data: {
              client: data.client,
              type: 'resource',
              userId: data.userId,
              data: {
                type: data.type || data.client,
                id: data.id,
                action: data.action,
              },
            },
          });
        },
      };
    }

    async authorized(request: IAuthorizedRequest): Promise<IAuthorizedResponse> {
      const data = <unknown>request as Record<string, unknown>;
      return this.requestResource('users.authorize', {
        method: RequestMethod.POST,
        tokenType: TokenTypes.ID,
        data: {
          client: data.client,
          type: 'resource',
          data: {
            type: data.type || data.client,
            id: data.id,
            action: data.action,
          },
        },
      });
    }

    async impersonate({ authScope, accountId, authSource }: IImpersonationRequest): Promise<IImpersonationResponse> {
      return this.requestResource('users.impersonate', {
        method: RequestMethod.POST,
        tokenType: TokenTypes.ID,
        data: {
          authScope,
          accountId,
          authSource,
        },
      });
    }
  }
}

export default AuthorizationService.Api;
